import { DatePicker, DayOfWeek, DefaultButton, Dropdown, IDropdownOption, Label, MessageBar, MessageBarType, PrimaryButton, ProgressIndicator, Stack, TextField } from "@fluentui/react"
import { useBoolean } from "@fluentui/react-hooks"
import { initializeIcons } from "@fluentui/react/lib/Icons"
import { FormEvent, useEffect, useState } from "react"
import { getTreatyYears } from "../../lib/helper"
import { makeApiCall } from "../../services/apiService"

interface BordereauxAdjustmentFormData {
	treaty_year: string
	reporting_period: Date | null
	coverage_period: Date | null
	group_code: string
	adjusting_account: string
	adjustment_reason: string
	adjustment_amount: string
}

initializeIcons()

const treatyYearOptions: IDropdownOption[] = getTreatyYears().map(ty => ({ key: ty.treatyYearId, text: `${ty.treatyYearId}: Starting ${ty.startDate.getMonth() + 1}/1/${ty.startDate.getFullYear()}` }))

const adjustingAccountOptions: IDropdownOption[] = [
	{ key: "", text: "Select Adjusting Account" },
	{ key: "GROUP SIR", text: "GROUP SIR" },
	{ key: "PREMIUM FUND", text: "PREMIUM FUND" },
	{ key: "CLAIM FUND", text: "CLAIM FUND" }
]

// TODO: not sure where group codes come from

export function BordereauxAdjustments() {
	const [formData, setFormData] = useState<BordereauxAdjustmentFormData>({
		treaty_year: "",
		reporting_period: null,
		coverage_period: null,
		group_code: "",
		adjusting_account: "",
		adjustment_reason: "",
		adjustment_amount: ""
	})

	const [isSubmitting, { setTrue: startSubmitting, setFalse: stopSubmitting }] = useBoolean(false)
	const [error, setError] = useState<string | null>(null)
	const [loadingError, setLoadingError] = useState<string | null>(null)
	const [successMessage, setSuccessMessage] = useState<string | null>(null)
	const [adjustmentReasons, setAdjustmentReasons] = useState<string[]>([])
	const [loadingReasons, setLoadingReasons] = useState<boolean>(true)

	const [groupCodes, setGroupCodes] = useState<string[]>([])

	useEffect(() => {
		const fetchAdjustmentReasons = async () => {
			try {
				setLoadingReasons(true)
				const reasons = await makeApiCall("getBordereauxReasons", "GET")
				const codes = await makeApiCall("getBordereauxGroupCodes", "GET")
				setGroupCodes(codes || [])
				setAdjustmentReasons(reasons || [])
			} catch (err) {
				console.error("Error from API:", err)
				setLoadingError("Failed to load. See console for more details.")
			} finally {
				setLoadingReasons(false)
			}
		}

		fetchAdjustmentReasons()
	}, [])

	const handleInputChange = (field: keyof BordereauxAdjustmentFormData, value: any) => {
		setFormData(prevData => ({
			...prevData,
			[field]: value
		}))

		setError(null)
		setSuccessMessage(null)
	}

	const handleSubmit = async (e: FormEvent) => {
		e.preventDefault()

		if (!formData.treaty_year) {
			setError("Treaty Year is required")
			return
		}

		if (!formData.reporting_period) {
			setError("Reporting Period is required")
			return
		}

		if (!formData.adjusting_account) {
			setError("Adjusting Account is required")
			return
		}

		if (!formData.adjustment_reason) {
			setError("Adjustment Reason is required")
			return
		}

		if (!formData.adjustment_amount) {
			setError("Adjustment Amount is required")
			return
		}

		const formatDate = (date: Date | null) => {
			if (!date) return null
			return date.toISOString().split("T")[0]
		}

		try {
			startSubmitting()
			setError(null)

			const userId = (window.top as any).Xrm.Page.context.userSettings.userId.substring(1, 37)

			const payload = {
				treaty_year: parseFloat(formData.treaty_year),
				reporting_period: formatDate(formData.reporting_period),
				coverage_period: formatDate(formData.coverage_period),
				group_code: formData.group_code,
				adjusting_account: formData.adjusting_account,
				adjustment_reason: formData.adjustment_reason,
				adjustment_amount: parseFloat(formData.adjustment_amount),
				userid: userId
			}

			await makeApiCall("submitBordereauxAdjustment", "POST", JSON.stringify(payload))

			setFormData({
				treaty_year: "",
				reporting_period: null,
				coverage_period: null,
				group_code: "",
				adjusting_account: "",
				adjustment_reason: "",
				adjustment_amount: ""
			})

			setSuccessMessage("Bordereaux adjustment submitted successfully!")
		} catch (err) {
			console.error("Error submitting form:", err)
			setError("Failed to submit adjustment. Please try again or contact support.")
		} finally {
			stopSubmitting()
		}
	}

	return (
		<Stack tokens={{ childrenGap: 20 }} style={{ maxWidth: 800, margin: "0 auto", padding: 20 }}>
			<Stack.Item align="center">
				<h2>Bordereaux Adjustments</h2>
			</Stack.Item>

			{loadingReasons && <ProgressIndicator label="Loading form data..." />}

			{error && (
				<MessageBar messageBarType={MessageBarType.error} isMultiline={false} dismissButtonAriaLabel="Close" onDismiss={() => setError(null)}>
					{error}
				</MessageBar>
			)}

			{loadingError && (
				<MessageBar messageBarType={MessageBarType.error} isMultiline={false} dismissButtonAriaLabel="Close">
					{loadingError}
				</MessageBar>
			)}

			{successMessage && (
				<MessageBar messageBarType={MessageBarType.success} isMultiline={false} dismissButtonAriaLabel="Close" onDismiss={() => setSuccessMessage(null)}>
					{successMessage}
				</MessageBar>
			)}

			{!loadingError && (
				<form onSubmit={handleSubmit}>
					<Stack tokens={{ childrenGap: 15 }}>
						{/* Treaty Year */}
						<Stack.Item>
							<Label required htmlFor="treaty_year">
								Treaty Year
							</Label>
							<Dropdown
								id="treaty_year"
								options={treatyYearOptions}
								selectedKey={formData.treaty_year}
								onChange={(_, option) => option && handleInputChange("treaty_year", option.key as string)}
								disabled={isSubmitting || loadingReasons}
								required
							/>
						</Stack.Item>

						{/* Reporting Period */}
						<Stack.Item>
							<Label required htmlFor="reporting_period">
								Reporting Period
							</Label>
							<DatePicker
								id="reporting_period"
								value={formData.reporting_period || undefined}
								isMonthPickerVisible
								onSelectDate={date => handleInputChange("reporting_period", date)}
								firstDayOfWeek={DayOfWeek.Sunday}
								formatDate={date => (date ? `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, "0")}` : "")}
								disabled={isSubmitting || loadingReasons}
								allowTextInput
								ariaLabel="Required"
							/>
						</Stack.Item>

						{/* Coverage Period */}
						<Stack.Item>
							<Label htmlFor="coverage_period">Coverage Period (Optional)</Label>
							<DatePicker
								id="coverage_period"
								value={formData.coverage_period || undefined}
								onSelectDate={date => handleInputChange("coverage_period", date)}
								firstDayOfWeek={DayOfWeek.Sunday}
								formatDate={date => (date ? `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, "0")}` : "")}
								disabled={isSubmitting || loadingReasons}
								allowTextInput
							/>
						</Stack.Item>

						{/* Group Code */}
						<Stack.Item>
							<Label htmlFor="group_code">Group Code (Optional)</Label>
							<Dropdown
								id="group_code"
								options={groupCodes.map(code => ({ key: code, text: code }))}
								selectedKey={formData.group_code}
								onChange={(_, option) => option && handleInputChange("group_code", option.key as string)}
								disabled={isSubmitting || loadingReasons}
							/>
						</Stack.Item>

						{/* Adjusting Account */}
						<Stack.Item>
							<Label required htmlFor="adjusting_account">
								Adjusting Account
							</Label>
							<Dropdown
								id="adjusting_account"
								options={adjustingAccountOptions}
								selectedKey={formData.adjusting_account}
								onChange={(_, option) => option && handleInputChange("adjusting_account", option.key as string)}
								disabled={isSubmitting || loadingReasons}
								required
							/>
						</Stack.Item>

						{/* Adjustment Reason */}
						<Stack.Item>
							<Label required htmlFor="adjustment_reason">
								Adjustment Reason
							</Label>
							<Dropdown
								id="adjustment_reason"
								options={[
									{ key: "", text: "Select Adjustment Reason" },
									...adjustmentReasons.map(reason => ({
										key: reason,
										text: reason
									}))
								]}
								selectedKey={formData.adjustment_reason}
								onChange={(_, option) => option && handleInputChange("adjustment_reason", option.key as string)}
								disabled={isSubmitting || loadingReasons || adjustmentReasons.length === 0}
								required
							/>
						</Stack.Item>

						{/* Adjustment Amount */}
						<Stack.Item>
							<Label required htmlFor="adjustment_amount">
								Adjustment Amount
							</Label>
							<TextField
								id="adjustment_amount"
								prefix="$"
								value={formData.adjustment_amount}
								onChange={(_, newValue) => handleInputChange("adjustment_amount", newValue || "")}
								disabled={isSubmitting || loadingReasons}
								required
								type="number"
								step="0.01"
							/>
						</Stack.Item>

						{/* Submit Button */}
						<Stack.Item>
							<Stack horizontal tokens={{ childrenGap: 10 }}>
								<PrimaryButton type="submit" text="Submit Adjustment" disabled={isSubmitting || loadingReasons} />
								<DefaultButton
									text="Reset"
									onClick={() => {
										setFormData({
											treaty_year: "",
											reporting_period: null,
											coverage_period: null,
											group_code: "",
											adjusting_account: "",
											adjustment_reason: "",
											adjustment_amount: ""
										})
										setError(null)
										setSuccessMessage(null)
									}}
									disabled={isSubmitting || loadingReasons}
								/>
							</Stack>
						</Stack.Item>
					</Stack>
				</form>
			)}
		</Stack>
	)
}
